import { airtimeContacts as seedContacts } from '@/mocks/airtime';
import type { AirtimeContact } from '@/types/portal';

/**
 * Local persistence for the airtime lines a finance user tops up.
 *
 * The seed directory ships the agency's common numbers; anything the user adds
 * is stored on top of it in localStorage so saved lines and their pinned state
 * survive between sessions. Falls back to the seed set on first load or when
 * storage is unavailable.
 */
const STORAGE_KEY = 'twiva.airtimeContacts.v1';

/** Read the persisted airtime lines, falling back to the seed set. */
export function loadAirtimeContacts(): AirtimeContact[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return seedContacts;
    const parsed = JSON.parse(raw) as AirtimeContact[];
    if (!Array.isArray(parsed) || parsed.length === 0) return seedContacts;
    return parsed;
  } catch {
    return seedContacts;
  }
}

/** Persist the airtime lines; degrades quietly if storage is blocked. */
export function persistAirtimeContacts(contacts: AirtimeContact[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(contacts));
  } catch {
    // Storage unavailable (private mode) — the session keeps the in-memory copy.
  }
}

/** Build a new saved line from a name and number. New lines start unpinned. */
export function createAirtimeContact(name: string, number: string): AirtimeContact {
  return {
    id: `air-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    name: name.trim() || number,
    number,
    favourite: false,
  };
}